
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

interface QuizQuestion {
  type: string;
  question: string;
  options?: string[];
  correct_answer: number;
  explanation: string;
}

interface LessonQuizProps {
  questions: QuizQuestion[];
  onQuizComplete?: (passed: boolean) => void;
}

/**
 * LessonQuiz component that requires all questions to be answered correctly
 * before allowing lesson completion. Enhanced with better debugging and error handling.
 */
export const LessonQuiz = ({ questions, onQuizComplete }: LessonQuizProps) => {
  // Enhanced debugging for quiz questions
  console.log("🧪 LessonQuiz received questions:", {
    questionsCount: questions?.length || 0,
    questionsData: questions,
    hasQuestions: !!questions && questions.length > 0
  });

  // Safety check for questions array
  if (!questions || questions.length === 0) {
    console.warn("⚠️ No quiz questions provided to LessonQuiz component");
    return (
      <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-yellow-800">
            Knowledge Check
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-yellow-700">
            No knowledge check questions are available for this lesson.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Track selected answers for each question
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>(
    new Array(questions.length).fill(-1)
  );
  
  // Track which questions show explanations
  const [showExplanations, setShowExplanations] = useState<boolean[]>(
    new Array(questions.length).fill(false)
  );
  
  // Track if quiz has been completed successfully
  const [quizCompleted, setQuizCompleted] = useState(false);

  /**
   * Handle answer selection for a specific question
   */
  const handleAnswer = (questionIndex: number, answerIndex: number) => {
    if (quizCompleted) return;

    console.log("📝 Answer selected:", { questionIndex, answerIndex, correctAnswer: questions[questionIndex].correct_answer });

    // Update selected answers
    const newAnswers = [...selectedAnswers];
    newAnswers[questionIndex] = answerIndex;
    setSelectedAnswers(newAnswers);

    // Show explanation for this question
    const newExplanations = [...showExplanations];
    newExplanations[questionIndex] = true;
    setShowExplanations(newExplanations);

    // Check if all questions have been answered
    if (newAnswers.every(answer => answer !== -1)) {
      // Calculate how many answers are correct
      const correctAnswers = newAnswers.reduce((acc, answer, index) => {
        return acc + (answer === questions[index].correct_answer ? 1 : 0);
      }, 0);
      
      const allCorrect = correctAnswers === questions.length;
      
      console.log("🏁 Quiz completion check:", { correctAnswers, totalQuestions: questions.length, allCorrect });
      
      if (allCorrect) {
        // All answers correct - quiz passed
        setQuizCompleted(true);
        toast.success("Perfect! All answers correct! 🎉", {
          description: "You can now complete the lesson."
        });
        onQuizComplete?.(true);
      } else {
        // Some answers wrong - show feedback but don't allow completion
        toast.error("Some answers are incorrect", {
          description: `You got ${correctAnswers} out of ${questions.length} correct. Review the explanations and try again.`
        });
        
        // Reset to allow retry
        setTimeout(() => {
          setSelectedAnswers(new Array(questions.length).fill(-1));
          setShowExplanations(new Array(questions.length).fill(false));
        }, 3000);
      }
    }
  };

  return (
    <Card className="bg-gradient-to-br from-purple-50 to-pink-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Knowledge Check
          {quizCompleted && <CheckCircle2 className="text-green-500 h-5 w-5" />}
        </CardTitle>
        <p className="text-sm text-gray-600">
          Answer all questions correctly to complete the lesson ({questions.length} questions)
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {questions.map((question, qIndex) => (
          <div key={qIndex} className="space-y-4">
            <h4 className="font-semibold text-lg">
              {qIndex + 1}. {question.question}
            </h4>
            
            {question.type === "multiple_choice" && question.options && (
              <div className="space-y-2">
                {question.options.map((option, oIndex) => {
                  const isSelected = selectedAnswers[qIndex] === oIndex;
                  const isCorrect = oIndex === question.correct_answer;
                  const showResult = showExplanations[qIndex];
                  
                  let buttonVariant: "default" | "outline" | "destructive" = "outline";
                  let buttonClass = "";
                  
                  if (isSelected && showResult) {
                    if (isCorrect) {
                      buttonVariant = "default";
                      buttonClass = "bg-green-500 hover:bg-green-600 text-white";
                    } else {
                      buttonVariant = "destructive";
                    }
                  } else if (showResult && isCorrect) {
                    buttonClass = "border-green-500 text-green-700 bg-green-50";
                  } else if (isSelected) {
                    buttonVariant = "default";
                  }
                  
                  return (
                    <Button
                      key={oIndex}
                      variant={buttonVariant}
                      onClick={() => handleAnswer(qIndex, oIndex)}
                      disabled={quizCompleted}
                      className={`w-full justify-start text-left ${buttonClass}`}
                    >
                      {option}
                    </Button>
                  );
                })}
              </div>
            )}

            {showExplanations[qIndex] && (
              <div className="mt-4 p-4 bg-background rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  {selectedAnswers[qIndex] === question.correct_answer ? (
                    <CheckCircle2 className="text-green-500 h-5 w-5" />
                  ) : (
                    <XCircle className="text-red-500 h-5 w-5" />
                  )}
                  <span className={selectedAnswers[qIndex] === question.correct_answer ? "text-green-700 font-medium" : "text-red-700 font-medium"}>
                    {selectedAnswers[qIndex] === question.correct_answer ? "Correct!" : "Incorrect"}
                  </span>
                </div>
                <p className="text-gray-700">{question.explanation}</p>
              </div>
            )}
          </div>
        ))}
        
        {/* Show completion status */}
        {selectedAnswers.every(answer => answer !== -1) && !quizCompleted && (
          <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800">
              Please review the incorrect answers and try again to complete the lesson.
            </p>
          </div>
        )}

        {/* Show success message when completed */}
        {quizCompleted && (
          <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">
              🎉 Excellent work! You've successfully completed the knowledge check.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
